import React, { useEffect } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { SidebarProvider } from 'src/state/sidebar';
import Aside from 'src/pages/SignedInPage/Aside';
import AssessmentsPage from 'src/pages/AssessmentsPage';
import ErrorPage from 'src/pages/ErrorPage';
import AssessmentPage from 'src/pages/AssessmentPage';
import { AssessmentProvider } from 'src/state/assessment';
import ScenarioPage from 'src/pages/ScenarioPage';
import { ScenarioProvider } from 'src/state/scenario';
import GetStarted from '../GetStarted';
import CompareAssessment from '../compare/CompareAssessment';
import ScenarioLibraryPage, { ScenarioGroupPage, ScenarioLibraryAllPage } from '../ScenarioLibraryPage';
import IncidentsPage from '../IncidentsPage';
import CompareScenario from '../compare/CompareScenario';
import FrameworkLibrariesPage from '../FrameworkLibrariesPage';
import RiskManagementPage from '../RiskManagementPage';
import ReportCentrePage from '../ReportCentrePage';
import ControlPage from '../ControlPage';
import ControlLibraryPage from '../ControlLibraryPage';
import ControlsLibrariesPage from '../ControlsLibrariesPage';
import { useAuth } from 'src/state/auth';
import WorkspaceSettingsScreen from '../workspace/WorkspaceSettingsScreen';
import IncidentPage from '../IncidentPage';
import HeaderV2 from 'src/components/HeaderV2';
import OrganisationHierarchyPage from '../organisation/OrganisationHierarchyPage';
import WorkspaceAuditPage from 'src/pages/workspace/WorkspaceAuditPage';
import OrganisationSettingsPage from '../organisation/OrganisationSettingsPage';
import DashboardPage from '../DashboardPage';
import { TscsModal } from 'src/components/modals';
import Beamer from './Beamer';
import { useStateSelector } from 'src/redux';
import Loader from 'src/components/Loader/Loader';
import useNavLocation from 'src/hooks/useNavLocation';
import Main from './Main';
import { makeLogout } from 'src/utils/auth';
import { WizzardWorkspace } from 'src/components/Wizzard';
import ScenarioTemplatePage from '../ScenarioTemplatePage';
import { useSuggestions } from 'src/hooks/useSuggestions';
import { useUserflow } from 'src/utils/userflow';
import SessionManager from './SessionManager';
import MaintenanceModeRecovery from './MaintenanceModeRecovery';
import ExportRunnerDebugButton from 'src/components/ExportRunnerDebugButton';
import InsightsPage from '../InsightsPage';
import WizardRecomendedControls from 'src/components/Wizzard/RecomendedControls';
import FrameworkLibrabryPage9 from '../FrameworkLibrabryPage/FrameworkLibrabryPage9';
import { AccessGuard } from './AccessGuard';
import { useAuth0 } from '@auth0/auth0-react';

const LogoutPage: React.FC = () => {
  const { logout: logoutAuth0 } = useAuth0();

  useEffect(() => {
    makeLogout({ noEvents: true });
    logoutAuth0({ logoutParams: { returnTo: `${window.location.protocol}//${window.location.host}/auth` } });
  }, []);

  return null;
};

const SignedInPage = () => {
  const { isSignedIn, isLoading, appLoaded, features } = useAuth();
  const isMaintenanceMode = useStateSelector(({ app }) => app.isMaintenanceMode);
  useNavLocation();
  useSuggestions({ init: true });
  useUserflow();

  if (isLoading) return <Loader />;
  if (!isSignedIn) return <Navigate to="/auth" replace />;
  if (!appLoaded || isMaintenanceMode)
    return (
      <>
        <MaintenanceModeRecovery />
        <Loader />
      </>
    );

  return (
    <>
      <WizzardWorkspace />
      <WizardRecomendedControls />
      <TscsModal />
      <Beamer />
      <SessionManager />
      {/* <ExportRunnerDebugButton /> */}
      <AssessmentProvider>
        <ScenarioProvider>
          <SidebarProvider>
            <Aside />
            <Main>
              <HeaderV2 />
              <Routes>
                <Route path="/" element={<Navigate to="/risk-assessments" replace />} />
                <Route
                  path="/timeline"
                  element={
                    <AccessGuard requiredPermissions={{ orgUnit: ['canAccess'] }}>
                      <DashboardPage />
                    </AccessGuard>
                  }
                />
                <Route
                  path="/insights"
                  element={
                    <AccessGuard requiredPermissions={{ orgUnit: ['canAccess'] }}>
                      <InsightsPage />
                    </AccessGuard>
                  }
                />
                <Route
                  path="/risk-assessments"
                  element={
                    <AccessGuard requiredPermissions={{ orgUnit: ['canAccess'] }}>
                      <AssessmentsPage />
                    </AccessGuard>
                  }
                />
                <Route
                  path="/risk-assessments/:assessmentId"
                  element={
                    <AccessGuard requiredPermissions={{ orgUnit: ['canAccess'] }}>
                      <AssessmentPage />
                    </AccessGuard>
                  }
                />
                <Route
                  path="/risk-assessments/:assessmentId/scenario/:scenarioId"
                  element={
                    <AccessGuard requiredPermissions={{ orgUnit: ['canAccess'] }}>
                      <ScenarioPage />
                    </AccessGuard>
                  }
                />

                <Route
                  path="/scenario-library"
                  element={
                    <AccessGuard requiredPermissions={{ orgUnit: ['canAccess'] }}>
                      <ScenarioLibraryPage />
                    </AccessGuard>
                  }
                />
                <Route
                  path="/scenario-library/all"
                  element={
                    <AccessGuard requiredPermissions={{ orgUnit: ['canAccess'] }}>
                      <ScenarioLibraryAllPage />
                    </AccessGuard>
                  }
                />
                <Route
                  path="/scenario-library/groups/:scenarioGroupId"
                  element={
                    <AccessGuard requiredPermissions={{ orgUnit: ['canAccess'] }}>
                      <ScenarioGroupPage />
                    </AccessGuard>
                  }
                />
                <Route
                  path="/scenario-library/templates/:scenarioTemplateId"
                  element={
                    <AccessGuard requiredPermissions={{ orgUnit: ['canAccess'] }}>
                      <ScenarioTemplatePage />
                    </AccessGuard>
                  }
                />
                <Route
                  path="/scenario-library/:scenarioTemplateId"
                  element={
                    <AccessGuard requiredPermissions={{ orgUnit: ['canAccess'] }}>
                      <ScenarioTemplatePage />
                    </AccessGuard>
                  }
                />

                <Route
                  path="/control-frameworks"
                  element={
                    <AccessGuard requiredPermissions={{ orgUnit: ['canAccess'] }}>
                      <FrameworkLibrariesPage />
                    </AccessGuard>
                  }
                />
                <Route
                  path="/control-frameworks/all-controls"
                  element={
                    <AccessGuard requiredPermissions={{ orgUnit: ['canAccess'] }}>
                      <ControlsLibrariesPage />
                    </AccessGuard>
                  }
                />
                <Route
                  path="/control-frameworks/:frameworklibraryId"
                  element={
                    <AccessGuard requiredPermissions={{ orgUnit: ['canAccess'] }}>
                      <FrameworkLibrabryPage9 />
                    </AccessGuard>
                  }
                />
                <Route
                  path="/control-frameworks/all-controls/:control_identifier"
                  element={
                    <AccessGuard requiredPermissions={{ orgUnit: ['canAccess'] }}>
                      <ControlLibraryPage />
                    </AccessGuard>
                  }
                />

                <Route
                  path="/risk-management"
                  element={
                    <AccessGuard requiredPermissions={{ orgUnit: ['canAccess'] }}>
                      <RiskManagementPage />
                    </AccessGuard>
                  }
                />
                <Route
                  path="/risk-management/control/:controlId"
                  element={
                    <AccessGuard requiredPermissions={{ orgUnit: ['canAccess'] }}>
                      <ControlPage />
                    </AccessGuard>
                  }
                />
                <Route
                  path="/incidents"
                  element={
                    <AccessGuard requiredPermissions={{ orgUnit: ['canAccess'] }}>
                      <IncidentsPage />
                    </AccessGuard>
                  }
                />
                <Route
                  path="/report-centre"
                  element={
                    <AccessGuard requiredPermissions={{ orgUnit: ['canAccess'] }}>
                      <ReportCentrePage />
                    </AccessGuard>
                  }
                />
                <Route
                  path="/incidents/:incidentId"
                  element={
                    <AccessGuard requiredPermissions={{ orgUnit: ['canAccess'] }}>
                      <IncidentPage />
                    </AccessGuard>
                  }
                />

                {/* COMPARE */}
                <Route
                  path="/compare/assessments/:idA/:idB"
                  element={
                    <AccessGuard requiredPermissions={{ orgUnit: ['canAccess'] }}>
                      <CompareAssessment />
                    </AccessGuard>
                  }
                />
                <Route
                  path="/compare/scenarios/:scenarioAId/:scenarioBId"
                  element={
                    <AccessGuard requiredPermissions={{ orgUnit: ['canAccess'] }}>
                      <CompareScenario />
                    </AccessGuard>
                  }
                />

                {/* ORGANISATION */}
                <Route path="/home" element={<OrganisationHierarchyPage />} />
                <Route
                  path="/organisation/:id?"
                  element={
                    <AccessGuard requiredPermissions={{ orgUnit: ['canSeeSettings'] }} activeOrgUnitRecordType="O">
                      <OrganisationSettingsPage />
                    </AccessGuard>
                  }
                />

                {/* WORKSPACE */}
                <Route
                  path="/audit/workspace"
                  element={
                    <AccessGuard requiredPermissions={{ orgUnit: ['canSeeAuditLogs'] }}>
                      <WorkspaceAuditPage />
                    </AccessGuard>
                  }
                />
                <Route
                  path="/workspace/:id?"
                  element={
                    <AccessGuard requiredPermissions={{ orgUnit: ['canSeeSettings'] }}>
                      <WorkspaceSettingsScreen />
                    </AccessGuard>
                  }
                />

                {features['app.help.page'] && <Route path="/get-started" element={<GetStarted />} />}

                <Route path="/error" element={<ErrorPage />} />

                <Route path="/logout" element={<LogoutPage />} />
                <Route path="*" element={<ErrorPage status={404} />} />
              </Routes>
            </Main>
          </SidebarProvider>
        </ScenarioProvider>
      </AssessmentProvider>
    </>
  );
};

export default SignedInPage;
